import React, { useEffect, useState } from 'react';
import {
  Button, Flex, Spin, message,
} from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import axios from 'axios';
import I18n from 'i18n-js';
import { arrayOf, func, number } from 'prop-types';
import { ticket_linked_tickets_path, ticket_linked_ticket_path } from 'js-routes/generated/routes';
import LinkedTicketCard from './nested/LinkedTicketCard';
import LinkTicketPalette from './nested/LinkTicketPalette';
import { selectedNodePropTypes } from './propTypes';

const LinkedTickets = ({ ticketId, tickets = [], onOpen = (_ticket) => {} }) => {
  const [linkedTicketIds, setLinkedTicketIds] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [canManage, setCanManage] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [isSearching, setIsSearching] = useState(false);

  useEffect(() => {
    let active = true;
    const loadLinkedTickets = async () => {
      try {
        const { data } = await axios.get(ticket_linked_tickets_path(ticketId), {
          headers: { Accept: 'application/json' },
        });
        if (!active) return;

        setLinkedTicketIds(data.linkedTicketIds);
        setCanManage(data.canManage);
      } catch (error) {
        if (active) console.error(error); // eslint-disable-line no-console
      } finally {
        if (active) setIsLoading(false);
      }
    };
    void loadLinkedTickets();
    return () => {
      active = false;
    };
  }, [ticketId]);

  const changeLinks = async (method, url, data) => {
    setIsSending(true);
    try {
      const response = await axios({
        method,
        url,
        data,
        headers: { Accept: 'application/json' },
        responseType: 'json',
      });
      setLinkedTicketIds(response.data.linkedTicketIds);
      setIsSearching(false);
    } catch {
      message.error(I18n.t('tickets.form.linked_tickets_failed'));
    } finally {
      setIsSending(false);
    }
  };

  const handleLink = (linkedTicketId) => {
    void changeLinks('post', ticket_linked_tickets_path(ticketId), {
      linked_ticket_id: linkedTicketId, // eslint-disable-line camelcase
    });
  };

  const handleUnlink = (linkedTicketId) => {
    void changeLinks('delete', ticket_linked_ticket_path(ticketId, linkedTicketId));
  };

  const ticketsById = new Map(tickets.map((ticket) => [ticket.id, ticket]));
  const linkedTickets = linkedTicketIds.map((id) => ticketsById.get(id)).filter(Boolean);
  const candidates = tickets
    .filter((ticket) => ticket.id !== ticketId && !linkedTicketIds.includes(ticket.id));

  return (
    <div className="tickets-index-page__linked-tickets">
      <div className="tickets-index-page__linked-tickets-label">
        {I18n.t('tickets.form.linked_tickets')}
        {linkedTickets.length > 0 && (
          <span className="tickets-index-page__status-item-count">{linkedTickets.length}</span>
        )}
      </div>

      {isLoading && (
        <Flex justify="center">
          <Spin size="small" />
        </Flex>
      )}

      {linkedTickets.map((ticket) => (
        <LinkedTicketCard
          key={ticket.id}
          ticket={ticket}
          canManage={canManage}
          disabled={isSending}
          onOpen={onOpen}
          onUnlink={handleUnlink}
        />
      ))}

      {canManage && isSearching && (
        <LinkTicketPalette
          candidates={candidates}
          disabled={isSending}
          onLink={handleLink}
          onClose={() => setIsSearching(false)}
        />
      )}

      {canManage && !isSearching && (
        <Button
          type="dashed"
          icon={<PlusOutlined />}
          block
          className="tickets-index-page__external-link-add-btn"
          onClick={() => setIsSearching(true)}
        >
          {I18n.t('tickets.form.link_ticket')}
        </Button>
      )}
    </div>
  );
};

LinkedTickets.propTypes = {
  ticketId: number.isRequired,
  tickets: arrayOf(selectedNodePropTypes),
  onOpen: func,
};

export default LinkedTickets;
